import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLang } from "@/lib/lang";
import { streamEndpoint } from "@/lib/api";
import { invalidateConversations, invalidateConversation } from "@/lib/cache/invalidate";
import { useConversation } from "@/hooks/queries";
import ChatPromptChips from "@/components/ChatPromptChips";
import ChatContextDialog from "@/components/ChatContextDialog";
import CitationSourceDialog from "@/components/CitationSourceDialog";
import LibrarySearchResults, { getLibrarySearchHits } from "@/components/LibrarySearchResults";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles, Copy, Check, Files, Pencil } from "lucide-react";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";

export default function ChatPane({
  documents = [],
  chatContextDocIds = [],
  onChatContextChange,
  onDocumentUploaded,
  caseId = null,
  researchSessionId = null,
  hideContextPicker = false,
  allowEmptyContext = false,
  model,
  conversationId,
  onConversationCreated,
}) {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pendingSendMode, setPendingSendMode] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [liveCitations, setLiveCitations] = useState([]);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const abortRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(null);
  const [activeCitation, setActiveCitation] = useState(null);
  const [citationDialogOpen, setCitationDialogOpen] = useState(false);

  const { data: loadedConversation, isLoading: loadingHistory } = useConversation(conversationId);

  useEffect(() => {
    setStreamBuffer("");
    setLiveCitations([]);
    if (!conversationId) {
      setMessages([]);
      return;
    }
    setMessages(loadedConversation?.messages || []);
  }, [conversationId, loadedConversation]);

  const apiDocIds = useMemo(() => chatContextDocIds, [chatContextDocIds]);

  const contextDocCount = chatContextDocIds.length;

  const contextLabel = useMemo(() => {
    if (chatContextDocIds.length === 0) return t("chat_context_none");
    if (chatContextDocIds.length === 1) {
      const doc = documents.find((d) => d.id === chatContextDocIds[0]);
      return doc?.filename || `1 ${t("chat_context_count")}`;
    }
    return `${chatContextDocIds.length} ${t("chat_context_count")}`;
  }, [chatContextDocIds, documents, t]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamBuffer]);

  const ensureDocs = ({ librarySearch = false } = {}) => {
    if (allowEmptyContext || caseId || researchSessionId) return true;
    if (librarySearch) {
      if (documents.length === 0) {
        toast.error(t("chat_context_select_required"));
        setContextDialogOpen(true);
        return false;
      }
      return true;
    }
    if (chatContextDocIds.length === 0) {
      toast.error(t("chat_context_select_required"));
      setContextDialogOpen(true);
      return false;
    }
    return true;
  };

  const streamBody = (extra = {}) => ({
    ...extra,
    document_ids: caseId || researchSessionId ? [] : apiDocIds,
    model,
    ...(caseId ? { case_id: caseId } : {}),
    ...(researchSessionId ? { research_session_id: researchSessionId } : {}),
  });

  const runStream = async (body, userContent) => {
    if (streaming || !ensureDocs()) return;
    if (userContent) {
      setMessages((m) => [...m, { role: "user", content: userContent }]);
    }
    setStreaming(true);
    setStreamBuffer("");
    setLiveCitations([]);

    let accumulated = "";
    let messageCitations = [];
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamEndpoint(
        "/chat",
        streamBody(body),
        (evt) => {
          if (evt.conversation_id) {
            onConversationCreated?.(evt.conversation_id);
          }
          if (evt.sources) setLiveCitations(evt.sources);
          if (evt.citations) messageCitations = evt.citations;
          if (evt.delta) {
            accumulated += evt.delta;
            setStreamBuffer(accumulated);
          }
          if (evt.error) toast.error(evt.error);
        },
        controller.signal,
      );
      setMessages((m) => [
        ...m,
        { role: "assistant", content: accumulated, citations: messageCitations },
      ]);
      await invalidateConversations();
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setStreaming(false);
      setStreamBuffer("");
      abortRef.current = null;
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const mode = pendingSendMode;
    setInput("");
    setPendingSendMode(null);

    if (mode === "summary") {
      await runStream(
        {
          conversation_id: conversationId || null,
          message: text,
          summary_style: "summary",
        },
        text,
      );
      return;
    }

    if (mode === "library_search") {
      await runLibrarySearchWithQuery(text);
      return;
    }

    await runStream(
      {
        conversation_id: conversationId || null,
        message: text,
      },
      text,
    );
  };

  const handlePrefill = (text, { sendMode } = {}) => {
    setInput(text);
    setPendingSendMode(sendMode || null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const sendSummary = async () => {
    const label = t("summarize");
    await runStream(
      {
        conversation_id: conversationId || null,
        message: label,
        summary_style: "summary",
      },
      label,
    );
  };

  const openCitation = useCallback((citation) => {
    setActiveCitation(citation);
    setCitationDialogOpen(true);
  }, []);

  const runLibrarySearchWithQuery = async (query) => {
    if (!query) {
      toast.error(t("chat_search_library_hint"));
      return;
    }
    if (streaming || !ensureDocs({ librarySearch: true })) return;

    setMessages((m) => [...m, { role: "user", content: query }]);
    setStreaming(true);

    let searchPayload = null;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamEndpoint(
        "/chat",
        streamBody({
          conversation_id: conversationId || null,
          message: query,
          library_search: true,
        }),
        (evt) => {
          if (evt.conversation_id) {
            onConversationCreated?.(evt.conversation_id);
          }
          if (evt.library_search) {
            searchPayload = evt.library_search;
          }
          if (evt.error) toast.error(evt.error);
        },
        controller.signal,
      );

      const hits = searchPayload?.results || [];
      const aiAnswer = (searchPayload?.ai_answer || "").trim();
      const fallback =
        hits.length === 0
          ? t("library_search_none")
          : t("library_search_found").replace("{n}", String(hits.length));

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: aiAnswer || fallback,
          librarySearchResults: hits,
          citations: hits.map((hit, i) => ({
            kind: "library_hit",
            source_id: i + 1,
            document_id: hit.id,
            filename: hit.filename,
            page: hit.page,
            snippet: hit.snippet,
            matches: hit.matches,
            match_type: hit.match_type,
          })),
        },
      ]);
      await invalidateConversations();
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const runLibrarySearch = async () => {
    const query = input.trim();
    if (!query) {
      toast.error(t("chat_search_library_hint"));
      return;
    }
    setInput("");
    setPendingSendMode(null);
    await runLibrarySearchWithQuery(query);
  };

  const openLibraryHit = useCallback(
    (hit, sourceId) => {
      openCitation({
        source_id: hit.source_id || sourceId,
        document_id: hit.id,
        filename: hit.filename,
        page: hit.page,
        excerpt: hit.snippet,
      });
    },
    [openCitation],
  );

  const handleWorkflowComplete = useCallback(
    async (convId) => {
      if (convId) {
        onConversationCreated?.(convId);
        await invalidateConversation(undefined, convId);
      }
    },
    [onConversationCreated],
  );

  const copyMessage = async (content, idx) => {
    await navigator.clipboard.writeText(content);
    setCopied(idx);
    setTimeout(() => setCopied(null), 1500);
  };

  const showEmpty = messages.length === 0 && !streamBuffer && !loadingHistory;

  return (
    <div className="flex flex-col h-full bg-white" data-testid="chat-pane">
      {(!hideContextPicker || (!caseId && !researchSessionId)) && (
      <div className="grid-border-b px-4 py-3 bg-slate-50/50 space-y-3">
        {!hideContextPicker && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid="chat-context-btn"
              onClick={() => setContextDialogOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 grid-border bg-white hover:bg-slate-50 text-xs text-slate-700 transition-colors"
            >
              <Files size={13} className="text-slate-500" />
              <span>{contextLabel}</span>
              <Pencil size={11} className="text-slate-400" />
            </button>
          </div>
        )}
        {!caseId && !researchSessionId && (
          <ChatPromptChips
          contextDocCount={contextDocCount}
          streaming={streaming}
          onPrefill={handlePrefill}
        />
        )}
      </div>
      )}

      <ChatContextDialog
        open={contextDialogOpen}
        onOpenChange={setContextDialogOpen}
        documents={documents}
        selectedIds={chatContextDocIds}
        onSave={onChatContextChange}
        onDocumentUploaded={onDocumentUploaded}
      />

      <CitationSourceDialog
        open={citationDialogOpen}
        onOpenChange={setCitationDialogOpen}
        citation={activeCitation}
      />

      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5">
        {showEmpty && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12">
            <div className="w-12 h-12 grid-border bg-slate-50 flex items-center justify-center mb-4">
              <Sparkles size={20} strokeWidth={1.5} className="text-slate-700" />
            </div>
            <div className="font-serif-display text-2xl text-slate-900 mb-2">{t("new_chat")}</div>
            <p className="text-sm text-slate-500 max-w-md mb-6">
              {researchSessionId ? t("research_chat_hint") : t("chat_empty_hint")}
            </p>
            {!hideContextPicker && (
              <Button
                type="button"
                data-testid="chat-empty-choose-docs"
                onClick={() => setContextDialogOpen(true)}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Files size={14} />
                {t("chat_empty_action")}
              </Button>
            )}
          </div>
        )}

        {messages.map((m, idx) => (
          <div
            key={idx}
            data-testid={`chat-msg-${m.role}-${idx}`}
            className={["flex", m.role === "user" ? "justify-end" : "justify-start"].join(" ")}
          >
            <div className="max-w-[88%] group">
              {m.role === "user" ? (
                <div className="bg-slate-900 text-white px-4 py-2.5 text-sm leading-relaxed">
                  {m.content}
                </div>
              ) : (
                <div className="grid-border bg-white">
                  <div className="px-4 py-3">
                    <Markdown
                      text={m.content}
                      citations={(m.citations || []).filter((c) => c.kind !== "library_hit")}
                      citationLabel={t("citation")}
                      onCitationClick={openCitation}
                    />
                  </div>
                  {getLibrarySearchHits(m).length > 0 && (
                    <LibrarySearchResults
                      hits={getLibrarySearchHits(m)}
                      onOpenDocument={openLibraryHit}
                    />
                  )}
                  <div className="grid-border-t px-3 py-1.5 flex items-center justify-between bg-slate-50">
                    <span className="eyebrow !text-[9px]">{model}</span>
                    <button
                      data-testid={`copy-btn-${idx}`}
                      onClick={() => copyMessage(m.content, idx)}
                      className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-900 transition-colors"
                    >
                      {copied === idx ? <Check size={11} /> : <Copy size={11} />}
                      {copied === idx ? t("copied") : t("copy")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {streaming && streamBuffer && (
          <div className="flex justify-start">
            <div className="max-w-[88%] grid-border bg-white">
              <div className="px-4 py-3">
                <Markdown
                  text={streamBuffer}
                  streaming
                  citations={liveCitations}
                  citationLabel={t("citation")}
                  onCitationClick={openCitation}
                />
              </div>
            </div>
          </div>
        )}
        {streaming && !streamBuffer && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-pulse" />
            <span className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-pulse [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 bg-slate-900 rounded-full animate-pulse [animation-delay:300ms]" />
            <span className="text-xs ms-2">{t("streaming")}</span>
          </div>
        )}

        <div ref={endRef} />
      </div>

      <div className="grid-border-t p-4 bg-slate-50/50">
        {pendingSendMode && (
          <p className="text-[10px] text-slate-500 mb-2 uppercase tracking-wider">{t("chat_prefill_hint")}</p>
        )}
        <div className="grid-border bg-white">
          <Textarea
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (!e.target.value.trim()) setPendingSendMode(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t("ask_placeholder")}
            rows={3}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="border-0 focus-visible:ring-0 resize-none rounded-none text-sm bg-transparent"
            disabled={streaming}
          />
          <div className="grid-border-t flex items-center justify-between px-3 py-2">
            <div className="eyebrow !text-[9px]">⏎ {lang === "ar" ? "إرسال" : "send"} · ⇧⏎ {lang === "ar" ? "سطر جديد" : "newline"}</div>
            <Button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-8 px-4 text-xs gap-2"
            >
              <Send size={13} />
              {t("send")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
